import React, { useCallback } from "react";
import { useJsApiLoader } from "@react-google-maps/api";
import { MAP_TYPES, mapApiKey } from "../../const";
import LiveMap from "./LiveMap";
import LocationPicker from "./LocationPicker";
import StaticMap from "./StaticMap";

const libraries = ["places"];

const MapComponent = (
  {
    onLocationSelect,
    activeTab = "",
    initialLocations = [],
    showDirections = false,
    terminalId,
    type,
    handleClose,
    requestStartDate,
    wsConnection,
    pickLocation,
    vehicleId,
    vehicleDetails,
    locations,
    fetchedLocations,
    jobId,
    requestId,
    opConfirmStatusData,
    timeUpdateCompleted,
    lastDepartureTime,
    firstArrivalTime,
    onDirectionsChanged,
  },
  ref,
) => {
  const apiKey = mapApiKey;

  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: apiKey,
    libraries,
  });

  const mapContainerStyle =
    activeTab === "vehicle" || activeTab === "driver"
      ? {
          width: "auto",
          height: "80vh",
          display: "none",
          zIndex: "-1",
          position: "",
          top: "",
          left: "",
          right: "",
          bottom: "",
        }
      : activeTab === "rate-master"
        ? {
            width: "100%",
            height: "68vh",
          }
        : activeTab === "request-creation"
          ? {
              width: "50vw",
              height: "50vh",
            }
          : {
              width: "auto",
              height: "80vh",
              display: "flex",
              zIndex: "100",
              position: "absolute",
              top: "0",
              left: "0",
              right: "0",
              bottom: "0",
            };

  const adjustZoom = useCallback((location1, location2) => {
    // Earth's radius in meters
    const R = 6378137;
    const dLat = ((location2.lat - location1.lat) * Math.PI) / 180;
    const dLon = ((location2.lng - location1.lng) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((location1.lat * Math.PI) / 180) *
        Math.cos((location2.lat * Math.PI) / 180) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c; // Distance in meters between the two points

    // Approximate method to calculate zoom level based on distance
    if (distance < 30) return 15; // Very close
    if (distance < 100) return 14; // Close
    if (distance < 500) return 13; // Moderate distance
    if (distance < 1000) return 12; // Moderate to far
    if (distance < 2000) return 11; // Far
    if (distance < 4000) return 10; // Very far
    if (distance < 8000) return 9; // Very very far
    if (distance < 16000) return 8; // Extremely far
    return 7; // Beyond this consider as very extremely far
  }, []);

  const adjustZoomForMultipleLocations = useCallback((locations) => {
    if (locations.length < 2) return 15; // Default zoom for a single location

    // Earth's radius in meters
    const R = 6378137;
    let maxDistance = 0;

    for (let i = 0; i < locations.length; i++) {
      for (let j = i + 1; j < locations.length; j++) {
        const location1 = locations[i];
        const location2 = locations[j];

        const dLat = ((location2.lat - location1.lat) * Math.PI) / 180;
        const dLon = ((location2.lng - location1.lng) * Math.PI) / 180;
        const a =
          Math.sin(dLat / 2) * Math.sin(dLat / 2) +
          Math.cos((location1.lat * Math.PI) / 180) *
            Math.cos((location2.lat * Math.PI) / 180) *
            Math.sin(dLon / 2) *
            Math.sin(dLon / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        const distance = R * c; // Distance in meters between the two points

        maxDistance = Math.max(maxDistance, distance);
      }
    }

    // Approximate method to calculate zoom level based on maximum distance
    if (maxDistance < 30) return 15; // Very close
    if (maxDistance < 100) return 14; // Close
    if (maxDistance < 500) return 13; // Moderate distance
    if (maxDistance < 1000) return 12; // Moderate to far
    if (maxDistance < 2000) return 11; // Far
    if (maxDistance < 4000) return 10; // Very far
    if (maxDistance < 8000) return 9; // Very very far
    if (maxDistance < 16000) return 8; // Extremely far
    return 7; // Beyond this consider as very extremely far
  }, []);

  const calculateMapCenter = useCallback((locations) => {
    if (locations.length === 0) return null;

    const totalLat = locations.reduce((sum, loc) => sum + loc.lat, 0);
    const totalLng = locations.reduce((sum, loc) => sum + loc.lng, 0);

    return {
      lat: totalLat / locations.length,
      lng: totalLng / locations.length,
    };
  }, []);

  let mapComponent = <></>;

  switch (type) {
    case MAP_TYPES.STATIC: {
      mapComponent = (
        <StaticMap
          mapContainerStyle={mapContainerStyle}
          activeTab={activeTab}
          adjustZoom={adjustZoom}
          adjustZoomForMultipleLocations={adjustZoomForMultipleLocations}
          initialLocations={initialLocations}
          showDirections={showDirections}
          calculateMapCenter={calculateMapCenter}
          onDirectionsChanged={onDirectionsChanged}
          requestId={requestId}
          jobId={jobId}
        />
      );
      break;
    }
    case MAP_TYPES.PICK: {
      mapComponent = (
        <LocationPicker
          pickLocation={pickLocation}
          apiKey={apiKey}
          handleClose={handleClose}
          onLocationSelect={onLocationSelect}
        />
      );
      break;
    }
    case MAP_TYPES.LIVE: {
      mapComponent = (
        <LiveMap
          timeUpdateCompleted={timeUpdateCompleted}
          lastDepartureTime={lastDepartureTime}
          firstArrivalTime={firstArrivalTime}
          activeTab={activeTab}
          wsConnection={wsConnection}
          requestStartDate={requestStartDate}
          locations={locations}
          mapContainerStyle={mapContainerStyle}
          terminalId={terminalId}
          vehicleDetails={vehicleDetails}
          vehicleId={vehicleId}
          fetchedLocations={fetchedLocations}
          jobId={jobId}
          requestId={requestId}
          opConfirmStatusData={opConfirmStatusData}
          adjustZoom={adjustZoom}
          adjustZoomForMultipleLocations={adjustZoomForMultipleLocations}
          calculateMapCenter={calculateMapCenter}
        />
      );
      break;
    }
    default:
  }

  return <>{isLoaded ? mapComponent : <></>}</>;
};

export default MapComponent;
